import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import {
  groupsByUid,
  sortByName,
  useDeviceCounts,
  useGroups,
  usePeople,
} from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import { isTeachingRole } from "./staff";
import type { Locale, User, UserRole } from "@/types";

/**
 * Everyone in the institution.
 *
 * The table is the register: name, what they are, what they sign in with, which
 * groups, how many devices, when they were last seen. A teacher looking for one
 * student scans down a column of names, so the sort is by name in the reader's
 * own collation rather than by creation date.
 */

type StatusFilter = "all" | "active" | "disabled" | "unset";

interface Filters {
  search: string;
  role: UserRole | "all";
  group: string;
  status: StatusFilter;
  withDevices: boolean;
}

const EMPTY_FILTERS: Filters = {
  search: "",
  role: "all",
  group: "all",
  status: "all",
  withDevices: false,
};

export function PeoplePage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const { user } = useAuth();
  const { people, loading } = usePeople();
  const { groups } = useGroups();
  const devices = useDeviceCounts();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<
    "delete" | "reset" | "disable" | undefined
  >(undefined);

  const index = useMemo(() => groupsByUid(groups), [groups]);

  const shown = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    return sortByName(people, locale).filter((person) => {
      if (person.role === "spontar_admin") return false;
      // "Teacher" includes the owner: they teach too, and an owner filtering
      // for teachers should see themselves rather than wonder whether they
      // have to add themselves.
      if (filters.role === "teacher" ? !isTeachingRole(person) : filters.role !== "all" && person.role !== filters.role) {
        return false;
      }
      if (filters.status === "active" && (person.disabled || person.mustChangePassword)) return false;
      if (filters.status === "disabled" && !person.disabled) return false;
      if (filters.status === "unset" && !person.mustChangePassword) return false;
      if (filters.group !== "all") {
        const theirs = index.get(person.uid) ?? [];
        if (!theirs.some((group) => group.id === filters.group)) return false;
      }
      if (filters.withDevices && (devices.get(person.uid) ?? 0) === 0) return false;
      if (needle) {
        const haystack = `${person.displayName} ${person.username ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [people, filters, index, locale, devices]);

  const selectedPeople = shown.filter((person) => selected.has(person.uid));
  const count = selectedPeople.length;

  const toggle = (uid: string) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    setSelected(next);
  };

  const runBulk = async (action: NonNullable<typeof pending>, reason: string) => {
    try {
      for (const person of selectedPeople) {
        if (action === "delete") {
          await call(CALLABLES.deleteUser, { uid: person.uid, reason });
        } else if (action === "reset") {
          await call(CALLABLES.resetUserPassword, { uid: person.uid });
        } else {
          await call(CALLABLES.setUserDisabled, { uid: person.uid, disabled: true, reason });
        }
      }
      success("common.saved");
      setSelected(new Set());
    } catch (error) {
      showError(error);
    }
  };

  return (
    <SectionShell
      titleKey="people.title"
      actions={
        <>
          <Link
            to="/people/import"
            className="inline-flex min-h-10 items-center rounded-control border border-line bg-white
                       px-4 py-2 text-[14px] font-medium leading-5 text-ink hover:bg-surface-muted"
          >
            {t("people.import")}
          </Link>
          <Link
            to="/people/new"
            className="inline-flex items-center min-h-10 rounded-control bg-brand px-4 py-2
                       text-[14px] font-medium leading-5 text-white hover:bg-brand-hover"
          >
            {t("people.add")}
          </Link>
        </>
      }
    >
      <p className="max-w-2xl text-ink-muted">{t("people.intro")}</p>

      <div className="mt-6">
        <Filters filters={filters} onChange={setFilters} groups={groups} />
      </div>

      {count > 0 ? (
        <div className="card mt-4 flex flex-wrap items-center gap-2 px-4 py-3">
          <span className="text-sm text-ink">{t("people.selected", { count })}</span>
          <div className="flex-1" />
          <Button variant="secondary" onClick={() => setPending("reset")}>
            {t("people.bulk.resetPassword")}
          </Button>
          <Button variant="secondary" onClick={() => setPending("disable")}>
            {t("people.bulk.disable")}
          </Button>
          <Button variant="danger" onClick={() => setPending("delete")}>
            {t("people.bulk.delete")}
          </Button>
        </div>
      ) : null}

      <div className="mt-6">
        {loading ? (
          <p className="py-10 text-center text-sm text-ink-muted">{t("common.loading")}</p>
        ) : shown.length === 0 ? (
          <EmptyState titleKey="people.empty" bodyKey="people.emptyBody" />
        ) : (
          <PeopleTable
            people={shown}
            index={index}
            selected={selected}
            onToggle={toggle}
            locale={locale}
            devices={devices}
            meUid={user?.uid}
          />
        )}
      </div>

      {/*
        Every destructive bulk action is confirmed by typing the count. A plain
        "are you sure" is answered yes by reflex; typing 27 is not something
        anyone does while thinking about something else.
      */}
      <ConfirmDialog
        open={pending !== undefined}
        onClose={() => setPending(undefined)}
        onConfirm={(reason) => runBulk(pending!, reason)}
        titleKey={`people.confirm.${pending ?? "delete"}Title`}
        titleParams={{ count }}
        bodyKey={`people.confirm.${pending ?? "delete"}Body`}
        expected={String(count)}
        confirmKey="people.confirm.typeCount"
        mismatchKey="people.confirm.countMismatch"
        withReason={pending === "delete" || pending === "disable"}
        destructive={pending === "delete"}
      />
    </SectionShell>
  );
}

function Filters({
  filters,
  onChange,
  groups,
}: {
  filters: Filters;
  onChange: (filters: Filters) => void;
  groups: readonly { id: string; name: string }[];
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex-1 min-w-56">
        <span className="field-label">{t("people.search")}</span>
        <input
          type="search"
          className="field-input"
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
        />
      </label>

      <label>
        <span className="field-label">{t("people.filter.role")}</span>
        <select
          className="field-input"
          value={filters.role}
          onChange={(event) =>
            onChange({ ...filters, role: event.target.value as Filters["role"] })
          }
        >
          <option value="all">{t("people.filter.all")}</option>
          <option value="teacher">{t("role.teacher")}</option>
          <option value="student">{t("role.student")}</option>
          <option value="owner">{t("role.owner")}</option>
        </select>
      </label>

      <label>
        <span className="field-label">{t("people.filter.group")}</span>
        <select
          className="field-input"
          value={filters.group}
          onChange={(event) => onChange({ ...filters, group: event.target.value })}
        >
          <option value="all">{t("people.filter.all")}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-2 pb-2.5">
        <input
          type="checkbox"
          checked={filters.withDevices}
          onChange={(event) => onChange({ ...filters, withDevices: event.target.checked })}
        />
        <span className="text-sm text-ink-soft">{t("people.filter.hasDevices")}</span>
      </label>

      <label>
        <span className="field-label">{t("people.filter.status")}</span>
        <select
          className="field-input"
          value={filters.status}
          onChange={(event) =>
            onChange({ ...filters, status: event.target.value as StatusFilter })
          }
        >
          <option value="all">{t("people.filter.all")}</option>
          <option value="active">{t("people.status.active")}</option>
          <option value="disabled">{t("people.status.disabled")}</option>
          <option value="unset">{t("people.status.mustChangePassword")}</option>
        </select>
      </label>
    </div>
  );
}

function PeopleTable({
  people,
  index,
  selected,
  onToggle,
  locale,
  devices,
  meUid,
}: {
  people: readonly User[];
  index: Map<string, { id: string; name: string }[]>;
  selected: ReadonlySet<string>;
  onToggle: (uid: string) => void;
  locale: Locale;
  devices: Map<string, number>;
  meUid: string | undefined;
}) {
  const t = useT();

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[14px]">
        <caption className="sr-only">{t("people.title")}</caption>
        <thead>
          <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
            <th scope="col" className="h-9 w-10 px-3">
              <span className="sr-only">{t("people.selected", { count: 0 })}</span>
            </th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.name")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.role")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.username")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.groups")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.devices")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.lastActive")}</th>
            <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("people.column.status")}</th>
          </tr>
        </thead>
        <tbody>
          {people.map((person) => (
            <tr key={person.uid} className="border-b border-line last:border-0">
              <td className="h-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={selected.has(person.uid)}
                  onChange={() => onToggle(person.uid)}
                  aria-label={person.displayName}
                />
              </td>
              <td className="h-10 px-3 py-2.5">
                <Link
                  to={`/people/${person.uid}`}
                  className="font-medium text-ink underline-offset-2 hover:underline"
                >
                  {person.displayName}
                </Link>
              </td>
              <td className="h-10 px-3 py-2.5 text-ink-muted">
                {person.role === "owner" && person.uid === meUid
                  ? /* The owner reading their own row: marked, so they do not
                       add themselves a second time as a teacher. */
                    t("people.role.ownerYou")
                  : t(`role.${person.role}`)}
              </td>
              <td className="h-10 px-3 py-2.5 text-ink-muted">
                {person.username ?? (
                  /* A player holds an enrolled device and never signs in, so
                     there is no username to show. An em dash rather than an
                     empty cell, so the column reads as deliberate. */
                  <span title={t("people.noUsername")}>{t("people.noUsernameMark")}</span>
                )}
              </td>
              <td className="h-10 px-3 py-2.5 text-ink-muted">
                {(index.get(person.uid) ?? []).map((group) => group.name).join(", ")}
              </td>
              <td className="h-10 px-3 py-2.5 text-ink-muted">{devices.get(person.uid) ?? 0}</td>
              <td className="h-10 px-3 py-2.5 text-ink-muted">
                {person.lastActiveAt ? formatDate(locale, person.lastActiveAt) : t("people.never")}
              </td>
              <td className="h-10 px-3 py-2.5">
                {person.disabled ? (
                  <span className="text-danger">{t("people.status.disabled")}</span>
                ) : person.mustChangePassword ? (
                  <span className="text-warning">{t("people.status.mustChangePassword")}</span>
                ) : (
                  <span className="text-positive">{t("people.status.active")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
